/*
 * Copyright (c) CIRROM 2026.
 */

import { useRef, useState } from "react";

/**
 * The loading contract the record surfaces share.
 *
 * `undefined` = still loading, `null` = failed; anything else is the payload.
 * Three states rather than a boolean beside the data, because "loaded and
 * empty" and "never arrived" are different facts and the surfaces render them
 * differently.
 */
export type Loadable<T> = T | null | undefined;

/**
 * The reload half of that contract, for surfaces whose data is refetched for
 * two different reasons.
 *
 * A *loud* reload - opening a different record, or retrying after a failure -
 * clears what is on screen and shows the loading state, because none of it is
 * about the record being loaded. A *silent* one, fired after a write to
 * confirm what the server now holds, leaves the current view in place while
 * fresh data is on its way; blanking a table the steward is working in to
 * re-render the same rows a moment later is a flicker, not feedback.
 *
 * Changing record always wins over silence, which is why the decision is made
 * here rather than at the call site: a silent reload that arrives in the same
 * tick as a new id would otherwise show the previous record's data as though
 * it were the new one's.
 */
export const useLoadCycle = (idsKey: string) => {
  const [reloadToken, setReloadToken] = useState({ count: 0, silent: false });
  const lastIdsRef = useRef(idsKey);

  /**
   * Call once at the top of the load effect. Reports whether this run should
   * clear the current data and show the loading state.
   */
  const startLoad = () => {
    const idsChanged = lastIdsRef.current !== idsKey;
    lastIdsRef.current = idsKey;
    return { showLoading: !reloadToken.silent || idsChanged };
  };

  return {
    /** Belongs in the load effect's dependency array. */
    reloadToken,
    startLoad,
    reload: () =>
      setReloadToken((token) => ({ count: token.count + 1, silent: false })),
    reloadSilently: () =>
      setReloadToken((token) => ({ count: token.count + 1, silent: true })),
  };
};
